"use client"
import { useState, useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X, AlertTriangle, Settings } from 'lucide-react'
import Button from '@/components/ui/Button'

interface ServiceMeta {
  _id: string;
  serviceName: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  status: 'Draft' | 'Published';
}

interface ServiceMetaDeleteModalProps {
  isOpen: boolean
  onClose: () => void
  serviceMeta: ServiceMeta | null
  onConfirmDelete: (serviceMetaId: string) => Promise<void>
}

export default function ServiceMetaDeleteModal({ isOpen, onClose, serviceMeta, onConfirmDelete }: ServiceMetaDeleteModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleDelete = async () => {
    // Additional validation
    if (!serviceMeta || !serviceMeta._id || !serviceMeta.serviceName) {
      setError('Invalid service meta data. Cannot proceed with deletion.')
      return
    }

    // Prevent double submission
    if (isLoading) {
      return
    }

    setIsLoading(true)
    setError('')

    try {
      await onConfirmDelete(serviceMeta._id)
      onClose()
    } catch (err) {
      console.error('Error deleting service meta:', err)

      let errorMessage = 'Failed to delete service meta'
      if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (err instanceof Error) {
        // Handle specific error cases
        if (err.message.includes('404')) {
          errorMessage = 'Service meta not found. It may have already been deleted.'
        } else if (err.message.includes('403')) {
          errorMessage = 'You do not have permission to delete this service meta.'
        } else if (err.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.'
        } else {
          errorMessage = err.message
        }
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setError('')
      onClose()
    }
  }

  // Enhanced close handler for better UX
  const handleModalClose = (open: boolean) => {
    if (!open && !isLoading) {
      setError('')
      onClose()
    }
  }

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any pending operations
      setError('')
      setIsLoading(false)
    }
  }, [])

  // Reset error when modal closes
  useEffect(() => {
    if (!isOpen) {
       
      setError('')
    }
  }, [isOpen])

  // Early return if service meta data is invalid
  if (!serviceMeta || !serviceMeta._id || !serviceMeta.serviceName || !serviceMeta.slug) {
    console.warn('ServiceMetaDeleteModal: Invalid service meta data provided', serviceMeta)
    return null
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleModalClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl z-[101]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-service-meta-title"
          aria-describedby="delete-service-meta-description"
        >
          <div className={`p-6 ${isLoading ? 'pointer-events-none opacity-75' : ''}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title id="delete-service-meta-title" className="text-lg font-semibold text-gray-900">
                Delete Service Meta
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                  aria-label="Close delete service meta dialog"
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Warning Icon and Message */}
            <div className="flex items-start space-x-3 mb-6">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-10 w-10 text-red-500" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h3 id="delete-service-meta-description" className="text-lg font-medium text-gray-900 mb-2">
                  Delete Service Meta Entry
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this service meta entry? This action cannot be undone and all associated SEO metadata will be permanently removed.
                </p>

                {/* Service Meta Info */}
                <div className="bg-gray-50 rounded-lg p-4" role="region" aria-label="Service meta information to be deleted">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center" aria-hidden="true">
                      <Settings className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900" aria-label={`Service name: ${serviceMeta.serviceName}`}>{serviceMeta.serviceName}</div>
                      <div className="text-sm text-gray-500" aria-label={`Slug: ${serviceMeta.slug}`}>/{serviceMeta.slug}</div>
                      <div className="text-xs text-gray-400 capitalize" aria-label={`Status: ${serviceMeta.status}`}>{serviceMeta.status}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="assertive">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <p className="ml-2 text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <p className="ml-2 text-sm text-blue-700">Deleting service meta...</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3" role="group" aria-label="Delete service meta actions">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
                aria-label="Cancel service meta deletion"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleDelete}
                disabled={isLoading}
                aria-label={`Delete service meta ${serviceMeta.serviceName}`}
                aria-describedby="delete-service-meta-description"
              >
                {isLoading ? 'Deleting...' : 'Delete Service Meta'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
